'use client'

import { TextFieldCT } from '@/components'
import { Box, Stack, Typography } from '@mui/material'
import { useTheme } from 'next-themes'
import React, { useCallback, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { EmojiButton } from './components/emoji-button'
import { MediaPreview } from './components/media-preview'
import { UploadButton } from './components/upload-button'
import { useMediaUpload } from './hooks/use-media-upload'
interface IEditorReviewProps {
  placeholder?: string
  value: string
  onChange: (value: string) => void
  onMediaChange?: (mediaUrls: string[]) => void
  disabled?: boolean
  maxImages?: number
  maxVideos?: number
  acceptedImageTypes?: string[]
  acceptedVideoTypes?: string[]
  rows?: number
  showToolbar?: boolean
}

export const EditorReview = React.forwardRef<HTMLTextAreaElement, IEditorReviewProps>(
  (
    {
      placeholder = 'Hãy chia sẻ nhận xét cho sản phẩm này bạn nhé!',
      value,
      onChange,
      onMediaChange,
      disabled = false,
      maxImages = 5,
      maxVideos = 1,
      acceptedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
      acceptedVideoTypes = ['video/mp4', 'video/webm', 'video/ogg'],
      rows = 4,
      showToolbar = true,
    },
    ref
  ) => {
    const { theme } = useTheme()
    const imageInputRef = useRef<HTMLInputElement>(null)
    const videoInputRef = useRef<HTMLInputElement>(null)
    const [isFocused, setIsFocused] = useState(false)

    const { uploadedMedia, isSubmitting, addMedia, removeMedia, uploadMedia, reset } = useMediaUpload()

    // Xử lý chọn emoji
    const handleEmojiSelect = useCallback(
      (emoji: any) => {
        onChange(value + emoji.native)
      },
      [value, onChange]
    )

    // Xử lý upload media
    const handleMediaUpload = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files
        if (!files) return

        addMedia(files, maxImages, maxVideos, acceptedImageTypes, acceptedVideoTypes)

        // Reset input
        if (event.target) {
          event.target.value = ''
        }
      },
      [addMedia, maxImages, maxVideos, acceptedImageTypes, acceptedVideoTypes]
    )

    // Xử lý click button upload
    const handleUploadClick = useCallback((type: 'image' | 'video') => {
      if (type === 'image') {
        imageInputRef.current?.click()
      } else {
        videoInputRef.current?.click()
      }
    }, [])

    // Xử lý xóa media
    const handleRemoveMedia = useCallback(
      (id: string) => {
        removeMedia(id)
        // Cập nhật danh sách media cho parent component nếu có callback
        if (onMediaChange) {
          const updatedMedia = uploadedMedia.filter((m) => m.id !== id)
          const mediaUrls = updatedMedia.map((m) => m.preview)
          onMediaChange(mediaUrls)
        }
      },
      [removeMedia, uploadedMedia, onMediaChange]
    )

    // Lấy media URLs để trả về cho parent
    const getMediaUrls = useCallback(async () => {
      if (uploadedMedia.length === 0) return []

      try {
        const urls = await uploadMedia()
        return {
          url: urls,
          type: uploadedMedia[0].type,
        }
      } catch (error) {
        toast.error('Có lỗi xảy ra khi tải media')
        return []
      }
    }, [uploadedMedia, uploadMedia])

    // Export method để parent component có thể gọi
    React.useImperativeHandle(
      ref,
      () =>
        ({
          getMediaUrls,
          reset,
        }) as any
    )

    return (
      <Box
        sx={{
          width: '100%',
          borderRadius: '8px',
          border: isFocused ? '1px solid' : '1px solid ',
          borderColor: isFocused ? 'primary.main' : '#BBBBBA',
          transition: 'all 0.2s',
        }}
      >
        {/* Media Preview */}
        <MediaPreview uploadedMedia={uploadedMedia} onRemoveMedia={handleRemoveMedia} />

        {/* Textarea */}
        <TextFieldCT
          fullWidth
          multiline
          rows={rows}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          sx={{
            borderRadius: 0,
            '.MuiInputBase-root': {
              height: 'unset',
              borderRadius: uploadedMedia.length > 0 ? '0 0 8px 8px' : '8px',
              padding: 0,
              border: 'none',
            },
            height: 'unset',
            fontSize: '1em',
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiInputBase-input': {
              borderRadius: '0 !important',
            },
          }}
        />

        {/* Toolbar */}
        {showToolbar && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              px: 1.5,
              pb: 1,
              justifyContent: 'space-between',
            }}
          >
            <Stack direction="row" spacing={0} alignItems="center">
              <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>Thêm vào đánh giá của bạn:</Typography>
              <EmojiButton onEmojiSelect={handleEmojiSelect} theme={theme || 'light'} disabled={disabled} />
              <UploadButton
                uploadedMedia={uploadedMedia}
                maxImages={maxImages}
                maxVideos={maxVideos}
                onUploadClick={handleUploadClick}
                disabled={disabled}
              />
            </Stack>
          </Stack>
        )}

        {/* Hidden file inputs */}
        <input
          ref={imageInputRef}
          type="file"
          accept={acceptedImageTypes.join(',')}
          multiple
          onChange={handleMediaUpload}
          style={{ display: 'none' }}
        />
        <input
          ref={videoInputRef}
          type="file"
          accept={acceptedVideoTypes.join(',')}
          onChange={handleMediaUpload}
          style={{ display: 'none' }}
        />
      </Box>
    )
  }
)

EditorReview.displayName = 'EditorReview'
